// The remembered "where are you playing?" choice for BMO play — venue or home —
// kept for one pass TTL so a second bet/entry within it skips the picker and
// reuses the same site_id (a visible "playing at … · change" line reopens it).
//
// One module-scoped record mirrored to sessionStorage under a per-user key, so
// it survives a reload but not the tab, and a different account on the same
// device never inherits it. Storage is touched only on the client, inside
// try/catch (private mode / blocked storage just means "nothing remembered").
//
// Hydration discipline: the record is loaded from storage in onMounted, never
// at first access — `site` is null during SSR AND the first client render, so a
// remembered choice can't make the hydrated markup differ from the server's.
import { getCurrentInstance } from 'vue'
import { venueChoiceExpiry } from './useVenueLookup'

export interface PlaySite { siteId: number; name: string; expiresAt: string }
interface Stored extends PlaySite { key: string }

const STORAGE_PREFIX = 'probook.play_site:'

// ---- module-scoped, shared across all callers --------------------------------
const choice = ref<Stored | null>(null)
// bumped when the current choice expires, so `site` re-evaluates at that instant
// (a computed can't otherwise notice the clock passing expiresAt)
const tick = ref(0)
let hydratedKey: string | null = null
let expiryTimer: ReturnType<typeof setTimeout> | null = null

function parseMs(iso: string): number {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? 0 : t
}

function scheduleExpiry(expiresAt: string) {
  if (!import.meta.client) return
  if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null }
  const ms = parseMs(expiresAt) - Date.now() + 50
  if (ms > 0 && ms < 0x7fffffff) expiryTimer = setTimeout(() => { tick.value += 1 }, ms)
}

function hydrate(key: string) {
  if (!import.meta.client || hydratedKey === key) return
  hydratedKey = key
  choice.value = null
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return
    const v = JSON.parse(raw)
    if (typeof v?.siteId === 'number' && typeof v?.name === 'string' && typeof v?.expiresAt === 'string') {
      choice.value = { key, siteId: v.siteId, name: v.name, expiresAt: v.expiresAt }
      scheduleExpiry(v.expiresAt)
    }
  } catch { /* unreadable storage = nothing remembered */ }
}

function persist(key: string, v: PlaySite | null) {
  if (!import.meta.client) return
  try {
    if (v) sessionStorage.setItem(key, JSON.stringify(v))
    else sessionStorage.removeItem(key)
  } catch { /* storage blocked: the in-memory copy still covers this page life */ }
}

export function usePlaySite() {
  const { user } = useAuth()

  // the platform player id is the stable identity /api/me exposes (there is no
  // probook user id on the payload); email is the pre-registration fallback
  const key = () => `${STORAGE_PREFIX}${user.value?.player_id ?? user.value?.email ?? 'anon'}`

  // load post-hydration (see the header note); harmless no-op when re-run
  if (getCurrentInstance()) onMounted(() => hydrate(key()))

  // the unexpired choice for THIS user, or null. pure read — no storage access,
  // so it is safe in a template / computed during hydration.
  const site = computed<PlaySite | null>(() => {
    void tick.value
    const c = choice.value
    if (!c || c.key !== key()) return null
    if (parseMs(c.expiresAt) <= Date.now()) return null
    return { siteId: c.siteId, name: c.name, expiresAt: c.expiresAt }
  })

  // the "playing at … · change" line's text; null when nothing is remembered
  const playingAt = computed<string | null>(() => {
    const s = site.value
    if (!s) return null
    return s.siteId > 1 ? `playing at ${s.name}` : 'play at home'
  })

  // imperative read for submit handlers (always post-mount): makes sure the
  // store is hydrated for the current user first, then returns the live value
  function current(): PlaySite | null {
    hydrate(key())
    return site.value
  }

  // remember a choice. expiresAt defaults to the venue lookup's pass expiry for
  // a venue and one TTL from now for home (see venueChoiceExpiry).
  function set(siteId: number, name: string, expiresAt: string = venueChoiceExpiry(siteId)) {
    const k = key()
    hydratedKey = k
    const v: PlaySite = { siteId, name, expiresAt }
    choice.value = { key: k, ...v }
    persist(k, v)
    scheduleExpiry(expiresAt)
  }

  function clear() {
    const k = key()
    hydratedKey = k
    choice.value = null
    persist(k, null)
    if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null }
  }

  return { site, playingAt, current, set, clear }
}
